import { useState } from 'react';

import { useCurrentUser } from 'providers/cache/cachedDataHooks';
import { userService } from 'services/user/userService';
import { UserRequestDto } from 'services/user/userService.dto';
import { convertToThumbnail300 } from 'utils/thumbnails';

import { useUpdateMyProfileDetailsAlerts } from './useUpdateMyProfileDetailsAlerts';

export const useUpdateMyProfileDetails = () => {
  const { currentUser, refreshCurrentUser } = useCurrentUser();
  const { wrongUserAbout, wrongUserName, userDetailsUpdated, unknownError } = useUpdateMyProfileDetailsAlerts();
  const [isLoading, setIsLoading] = useState(false);

  const updateMyProfileDetails = async (userRequest: UserRequestDto) => {
    const { firstName, lastName, about, imageUrl } = userRequest;
    const updatedFirstName = firstName || currentUser?.firstName;
    const updatedLastName = lastName || currentUser?.lastName;
    const updatedAbout = about !== undefined ? about : currentUser?.about;
    const updatedImageUrl = imageUrl || currentUser?.imageUrl;
    const imageChanged = imageUrl !== undefined && imageUrl !== currentUser?.imageUrl;

    try {
      setIsLoading(true);

      const response = await userService.updateUserCurrent(
        updatedFirstName!,
        updatedLastName!,
        updatedAbout!,
        updatedImageUrl!
      );

      const newUserData = response.data;

      if (imageChanged && newUserData?.imageUrl) {
        await waitForImageToBeAvailable(convertToThumbnail300(newUserData.imageUrl));
      }

      refreshCurrentUser();
      userDetailsUpdated();
    } catch (error: any) {
      if (error.response?.status === 422) {
        const code = error.response.data?.errors?.code;
        if (code === 'ABOUT') {
          wrongUserAbout();
        } else if (code === 'NAME') {
          wrongUserName();
        } else {
          unknownError();
        }
      } else {
        unknownError();
      }
    } finally {
      setIsLoading(false);
    }
  };

  return { updateMyProfileDetails, isLoading };
};

const waitForImageToBeAvailable = async (url: string, maxRetries = 10, delayMs = 1000): Promise<boolean> => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      const res = await fetch(url, { method: 'HEAD' });
      if (res.ok) return true;
    } catch (err) {
      /* ignoring error*/
    }
    await new Promise(resolve => setTimeout(resolve, delayMs));
  }
  return false;
};
